import { useEffect, useState, useCallback, useRef } from 'react';
import { MessageSquare, FileText } from 'lucide-react';
import ContactAccountModal from './ContactAccountModal';
import MissionDetailModal from './MissionDetailModal';
import { api, getUploadsUrl, getGatewayUploadsUrl } from '../services/api';

const PAGE_SIZE = 8;
const WCOLORS = ['#e11d6f', '#2563eb', '#059669', '#d97706', '#7c3aed', '#0891b2'];
// Email du compte connecté (admin/commercial) depuis le token.
function tokenEmail(token) {
  try {
    let p = (token || '').split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    p += '='.repeat((4 - (p.length % 4)) % 4);
    return JSON.parse(atob(p)).email || null;
  } catch { return null; }
}
function WarnAvatar({ name, photo }) {
  const ini = (name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '!';
  const idx = [...(name || '!')].reduce((a, c) => a + c.charCodeAt(0), 0) % WCOLORS.length;
  const ring = { width: 26, height: 26, borderRadius: 13, boxShadow: '0 0 0 3px rgba(220,38,38,.55)' };
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      {photo
        ? <img src={photo} alt={name || ''} style={{ ...ring, objectFit: 'cover', background: '#e2e8f0' }} />
        : <span style={{ ...ring, background: WCOLORS[idx], color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{ini}</span>}
      <span style={{ position: 'absolute', right: -4, bottom: -4, width: 14, height: 14, borderRadius: 7, background: '#dc2626', color: 'white', fontSize: 10, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid white' }}>!</span>
    </span>
  );
}

// Onglet « Missions » de la fiche contact : gestion du compte (création / menu / actions)
// + liste des missions du contact. Appels directs au hub et à antennes-missions (token admin).
const MISSIONS_URL = (process.env.REACT_APP_MISSIONS_URL || '').replace(/\/+$/, '');
const GW = (process.env.REACT_APP_GATEWAY_URL || '').replace(/\/+$/, '');
const TYPES = { pickup: 'Pick up', livraison: 'Livraison', installation: 'Installation', desinstallation: 'Désinstallation', retour: 'Retour' };
const STATUTS = { a_venir: 'À venir', en_cours: 'En cours', termine: 'Terminé', valide: 'Validé', annule: 'Annulé' };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '');

export default function ContactMissionsTab({ contact, token }) {
  const [missions, setMissions] = useState({ loading: true, error: null, list: [] });
  const [account, setAccount] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [creds, setCreds] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [pdfMission, setPdfMission] = useState(null);
  const [emailMode, setEmailMode] = useState('sandbox');
  const [avatars, setAvatars] = useState({}); // user_id -> URL avatar (photo du compte)
  const menuRef = useRef(null);
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const myEmail = tokenEmail(token);

  const loadMissions = useCallback(() => {
    if (!MISSIONS_URL) { setMissions({ loading: false, error: 'URL des missions non configurée (REACT_APP_MISSIONS_URL).', list: [] }); return; }
    setMissions((s) => ({ ...s, loading: true }));
    fetch(`${MISSIONS_URL}/api/missions?contact_antenne_id=${contact.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then((d) => setMissions({ loading: false, error: null, list: d.data || [] }))
      .catch((e) => setMissions({ loading: false, error: e.message, list: [] }));
  }, [contact.id, token]);

  const loadAccount = useCallback(() => {
    if (!GW) { setAccount({ exists: false, _noGw: true }); return; }
    fetch(`${GW}/api/user-contact-antennes/account?contact_antenne_id=${contact.id}`, { headers })
      .then(async (r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then((d) => setAccount(d.data))
      .catch(() => setAccount({ exists: false, _error: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.id, token]);

  useEffect(() => { loadMissions(); loadAccount(); }, [loadMissions, loadAccount]);

  // Avatars (photo du compte) des auteurs d'avertissements affichés sur les cartouches.
  useEffect(() => {
    const ids = [...new Set(missions.list.map((m) => m.warning?.by_user_id).filter((v) => Number.isInteger(v)))];
    const missing = ids.filter((id) => !(id in avatars));
    if (missing.length === 0) return;
    api.get(`/users/avatars?ids=${missing.join(',')}`)
      .then((res) => {
        const map = {}; const data = res.data?.data || {};
        for (const id of missing) map[id] = data[id]?.user_photo ? getGatewayUploadsUrl(data[id].user_photo) : null;
        setAvatars((prev) => ({ ...prev, ...map }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missions.list]);
  useEffect(() => {
    if (!GW) return;
    fetch(`${GW}/api/settings`, { headers }).then((r) => r.json()).then((d) => setEmailMode(d.data?.contact_account_email_mode || 'sandbox')).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const doCreate = async () => {
    if (!contact.email) { setErr('Ce contact n\'a pas d\'email — impossible de créer le compte.'); setConfirmCreate(false); return; }
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${GW}/api/user-contact-antennes/provision`, {
        method: 'POST', headers,
        // photo_url : le hub la récupère pour en faire l'avatar du nouveau compte.
        body: JSON.stringify({ contact_antenne_id: contact.id, antenne_id: contact.antenne_id, nom: contact.nom, prenom: contact.prenom, email: contact.email, photo_url: contact.photo ? getUploadsUrl(contact.photo) : null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || ('HTTP ' + r.status));
      setCreds(d.data); loadAccount();
    } catch (e) { setErr(e.message); } finally { setBusy(false); setConfirmCreate(false); }
  };

  // Bannière d'avertissement selon le statut du compte.
  const warning = (() => {
    if (!account?.exists) return null;
    const ev = account.last_event;
    const by = ev?.by_name ? ` par ${ev.by_name}` : '';
    const on = ev?.created_at ? ` le ${fmtDateTime(ev.created_at)}` : '';
    if (account.etat === 'supprime') return { color: '#dc2626', bg: '#fef2f2', text: `Compte supprimé${by}${on}.`, comment: ev?.comment };
    if (account.etat === 'inactif') return { color: '#b45309', bg: '#fffbeb', text: `Compte désactivé${by}${on}.`, comment: ev?.comment };
    if (account.force_password_change && ev?.action === 'reset') return { color: '#1d4ed8', bg: '#eff6ff', text: `Compte réinitialisé${by}${on} — en attente de la 1ère connexion.`, comment: ev?.comment };
    return null;
  })();

  const card = { background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18 };

  return (
    <div className="missions-tab">
      {/* En-tête : titre + menu compte (si compte existant) */}
      <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Missions du contact</h3>
        {account?.exists && (
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button onClick={() => setMenuOpen((o) => !o)} title="Menu" style={{ border: '1px solid #e2e8f0', background: 'white', borderRadius: 8, width: 34, height: 34, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>⋮</button>
            {menuOpen && (
              <div style={{ position: 'absolute', right: 0, top: '110%', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 20px rgba(0,0,0,.12)', zIndex: 20, minWidth: 140 }}>
                <button data-perm="antennes:contacts.account_manage" onClick={() => { setMenuOpen(false); setModalOpen(true); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', background: 'white', cursor: 'pointer' }}>🔑 Compte</button>
              </div>
            )}
          </div>
        )}
      </div>

      {err && <p style={{ color: '#dc2626' }}>{err}</p>}

      {/* Pas de compte → cartouche de création */}
      {account && !account.exists && !creds && (
        <div style={{ ...card, marginBottom: 16 }}>
          <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Ce contact n'a pas encore de compte pour accéder à l'application.</p>
          {!contact.email && <p style={{ color: '#b45309', fontSize: 13 }}>⚠️ Aucun email renseigné sur ce contact.</p>}
          {account._noGw && <p style={{ color: '#b45309', fontSize: 13 }}>⚠️ Gateway non configurée (REACT_APP_GATEWAY_URL).</p>}
          <button data-perm="antennes:contacts.account_manage" disabled={busy || !contact.email || account._noGw} onClick={() => setConfirmCreate(true)}
            style={{ marginTop: 8, padding: '9px 16px', borderRadius: 8, border: 'none', background: '#059669', color: 'white', fontWeight: 600, cursor: 'pointer', opacity: (busy || !contact.email) ? 0.6 : 1 }}>
            Créer le compte
          </button>
        </div>
      )}

      {/* Identifiants après création */}
      {creds && (
        <div style={{ ...card, marginBottom: 16, borderColor: '#86efac', background: '#f0fdf4' }}>
          <b>Compte créé ✔</b>
          <p style={{ margin: '6px 0 0' }}>Identifiant : <b>{creds.login}</b> · Mot de passe temporaire : <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>{creds.temp_password}</code></p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#166534' }}>
            {creds.email?.sent
              ? (creds.email.mode === 'sandbox'
                ? `⚠️ Mode test : l'email (identifiant + mot de passe) a été envoyé à votre adresse ${creds.email.to}.`
                : `Email envoyé à ${creds.email.to}.`)
              : 'Email non envoyé — transmets ces identifiants manuellement.'}
          </p>
        </div>
      )}

      {/* Avertissement statut compte */}
      {warning && (
        <div style={{ background: warning.bg, border: `1px solid ${warning.color}33`, color: warning.color, borderRadius: 10, padding: '10px 12px', marginBottom: 16, fontSize: 13.5 }}>
          <b>{warning.text}</b>
          {warning.comment && <div style={{ marginTop: 4, color: '#334155' }}>« {warning.comment} »</div>}
        </div>
      )}

      {/* Liste des missions — cartouches (récent → ancien) */}
      {missions.loading && <p>Chargement des missions…</p>}
      {!missions.loading && missions.error && <p style={{ color: '#b91c1c' }}>Missions indisponibles : {missions.error}</p>}
      {!missions.loading && !missions.error && missions.list.length === 0 && <p style={{ color: '#64748b' }}>Aucune mission enregistrée par ce contact.</p>}
      {!missions.loading && missions.list.length > 0 && (() => {
        const pages = Math.ceil(missions.list.length / PAGE_SIZE);
        const shown = missions.list.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
        return (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {shown.map((m) => {
                const hasRemarks = (m.etats || []).some((e) => (e.remarques || '').trim());
                const addr = [m.client_adresse, m.client_cp, m.client_ville].filter(Boolean).join(', ');
                return (
                  <div key={m.id} onClick={() => setSelected(m)} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', background: '#f1f5f9', padding: '2px 8px', borderRadius: 999 }}>{TYPES[m.mission_type] || m.mission_type}</span>
                      {m.warning && (
                        <button title="Voir le motif" onClick={(e) => { e.stopPropagation(); setSelected(m); }} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}><WarnAvatar name={m.warning.by_nom} photo={avatars[m.warning.by_user_id]} /></button>
                      )}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{m.titre || TYPES[m.mission_type]}</div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>{m.borne?.borne_n ? `Borne ${m.borne.borne_n}${m.borne.borne_type ? ' · ' + m.borne.borne_type : ''}` : '—'}</div>
                    {(m.client_ref || addr) && (
                      <div style={{ fontSize: 13, color: '#334155' }}><b>{m.client_ref || '—'}</b>{addr ? ' — ' + addr : ''}</div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', paddingTop: 8, borderTop: '1px solid #f1f5f9', fontSize: 12, color: '#94a3b8' }}>
                      <span style={{ fontWeight: 700, color: '#475569' }}>{STATUTS[m.statut] || m.statut}</span>
                      <span>· {fmtDate(m.created_at)}</span>
                      <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                        {hasRemarks && <span title="Remarques" style={{ width: 9, height: 9, borderRadius: 5, background: '#2563eb', display: 'inline-block' }} />}
                        {m.comment_count > 0 && <span title="Commentaires" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#64748b' }}><MessageSquare size={14} /> {m.comment_count}</span>}
                        <button title="Voir le PDF" onClick={(e) => { e.stopPropagation(); setPdfMission(m); }} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', display: 'inline-flex', alignItems: 'center', padding: 0 }}><FileText size={16} /></button>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 14 }}>
                <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: 'white', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}>‹ Précédent</button>
                <span style={{ fontSize: 13, color: '#64748b' }}>Page {page + 1} / {pages}</span>
                <button disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: 'white', cursor: page >= pages - 1 ? 'default' : 'pointer', opacity: page >= pages - 1 ? 0.5 : 1 }}>Suivant ›</button>
              </div>
            )}
          </>
        );
      })()}

      {selected && (
        <MissionDetailModal mission={selected} missionsUrl={MISSIONS_URL} token={token} onClose={() => setSelected(null)} onChanged={loadMissions} />
      )}

      {/* Aperçu PDF embarqué (généré à la 1ère consultation côté serveur, puis caché) */}
      {pdfMission && (
        <div onClick={() => setPdfMission(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 920, height: '92vh', background: 'white', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #e2e8f0' }}>
              <b style={{ fontSize: 14 }}>PDF — {pdfMission.titre || TYPES[pdfMission.mission_type]}</b>
              <button onClick={() => setPdfMission(null)} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>&times;</button>
            </div>
            <iframe title="PDF mission" src={`${MISSIONS_URL}/api/missions/${pdfMission.id}/pdf?token=${encodeURIComponent(token)}`} style={{ flex: 1, width: '100%', border: 'none' }} />
          </div>
        </div>
      )}

      {/* Modale infos + actions compte */}
      {modalOpen && account?.exists && (
        <ContactAccountModal account={account} token={token} onChanged={loadAccount} onClose={() => setModalOpen(false)} />
      )}

      {/* Modale de confirmation personnalisée pour la création */}
      {confirmCreate && (
        <div onClick={() => setConfirmCreate(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 380, background: 'white', borderRadius: 14, padding: 22, textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 6px' }}>Créer un compte ?</h3>
            <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 14 }}>Un compte va être créé pour <b>{contact.prenom} {contact.nom}</b> et un mot de passe temporaire envoyé par email.</p>
            {emailMode !== 'real' && myEmail && (
              <p style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: 10, color: '#9a3412', fontSize: 12.5, margin: '0 0 16px', textAlign: 'left' }}>
                ⚠️ Application en mode test : l'email (identifiant + mot de passe) sera envoyé à <b>{myEmail}</b> (votre adresse), pas à celle du contact.
              </p>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirmCreate(false)} style={{ flex: 1, padding: 11, borderRadius: 10, border: '1px solid #cbd5e1', background: 'white', color: '#475569', fontWeight: 600, cursor: 'pointer' }}>Annuler</button>
              <button onClick={doCreate} disabled={busy} style={{ flex: 1, padding: 11, borderRadius: 10, border: 'none', background: '#059669', color: 'white', fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>Créer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
